<script setup lang="ts">
    import { ref, reactive, watch } from 'vue';
    import { getAccounts } from './User.access';
    import List from '@shared/components/List/List.vue';
    import Loading from '@shared/components/Loading.vue';
    import BSFormSearch from '@shared/components/BSFormSearch.vue';
    import { formatDate } from '@cwd/Shared/utils/formatDateTime';
    import { AccountListItem, AccountReference } from './AccountReference';
    import { debounce } from '@shared/utils';

    const props = defineProps<{
        userAccounts: AccountReference[];
    }>();

    const emit = defineEmits<{
        'add-account': [account: AccountListItem];
    }>();

    const addAccountState = reactive<{
        accounts: AccountListItem[];
        loadingAccounts: boolean;
        filter: string;
    }>({
        accounts: [],
        loadingAccounts: false,
        filter: '',
    });

    watch(
        () => addAccountState.filter,
        (newVal, oldVal) => {
            if (oldVal === undefined) return;

            debounce(
                async () => {
                    await loadAccounts();
                },
                { delay: 300 }
            );
        },
        { immediate: true }
    );

    async function loadAccounts() {
        addAccountState.loadingAccounts = true;
        await getAccounts(addAccountState.filter)
            .then((response) => {
                const existingAccountIds = new Set(props.userAccounts.map((account) => account.AccountId));
                addAccountState.accounts = response.Results.filter((account) => !existingAccountIds.has(account.AccountId));
            })
            .finally(() => {
                addAccountState.loadingAccounts = false;
            });
    }

    async function addAccount(account: AccountListItem) {
        emit('add-account', account);
        addAccountState.accounts = addAccountState.accounts.filter((a) => a.AccountId !== account.AccountId);
    }
</script>

<template>
    <div v-if="props.userAccounts.length > 0">
        <h4>Added accounts ({{ props.userAccounts.length }})</h4>
        <ul>
            <li v-for="account in props.userAccounts" :key="account.AccountId">{{ account.Name }}</li>
        </ul>
    </div>

    <Loading :show="addAccountState.loadingAccounts" loadingStyle="progress-bar"></Loading>

    <div class="custom-scrollbar mx-h-400">
        <List
            displayAs="table"
            itemId="AccountId"
            :items="addAccountState.accounts"
            :tableHeaders="['Account', 'Client Company', 'Created', '']"
            :serverSideFilter="true"
            :hideSearch="true">
            <template #search>
                <BSFormSearch
                    v-model="addAccountState.filter"
                    inputId="add-account-list-search"
                    class="search-input-width"
                    :disabled="false"
                    @reset="() => (addAccountState.filter = '')"></BSFormSearch>
            </template>
            <template #item="{ item: account, index: index }">
                <td>{{ account.Name }}</td>
                <td>{{ account.ClientCompany.Name }}</td>
                <td>{{ formatDate(account.Created) }}</td>
                <td class="autofit">
                    <button class="btn btn-primary btn-sm" @click="addAccount(account)">Add</button>
                </td>
            </template>
        </List>
    </div>
</template>
